import { useState } from "react";
import {
    EditButton,
    ShowButton,
    useCreate,
    useUpdate,
    useTranslate,
    useNotify,
    useRefresh,
    usePermissions,
} from "react-admin";
import { Box, Dialog, Typography, Button } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { EditView } from "./EditView";
import { ShowView } from "./ShowView";
import { handleHttpError } from "shared/src/utils/errorHandler";

export const MediaDialog = ({ mediaTools, setMediaTools, mediaImages = [], resource }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [update] = useUpdate();
    const [create] = useCreate();
    const refresh = useRefresh();
    const translate = useTranslate();
    const notify = useNotify();
    const { isLoading, permissions } = usePermissions();

    const createMedia = (inputData) => {
        const file = inputData.files;
        if (mediaTools.id) {
            update(
                "media",
                {
                    id: mediaTools.id,
                    data: {
                        shortname: inputData.shortname,
                        ...(inputData.url && { url: inputData.url }),
                        ...(inputData.content && {
                            content: inputData.content,
                        }),
                    },
                    previousData: mediaTools.record,
                },
                {
                    onSettled: () => {
                        setMediaTools({
                            edit: null,
                            type: null,
                            id: null,
                            record: null,
                        });
                        refresh();
                    },
                    onError: (e) => {
                        handleHttpError(e, notify);
                    },
                }
            );
        } else {
            create(
                "media",
                {
                    data: {
                        ...(resource === "products" && { product: inputData.id }),
                        ...(resource === "skus" && { sku: inputData.id }),
                        ...(resource === "damageReports" && { damageReport: inputData.id }),
                        shortname: inputData.shortname
                            ? inputData.shortname
                            : file.title,
                        public: true,
                        type: mediaTools.type,
                        ...(inputData.url && { url: inputData.url }),
                        ...(inputData.content && {
                            content: inputData.content,
                        }),
                        ...(file && {
                            mimetype: file.rawFile.type,
                            size: file.rawFile.size,
                            files: [file],
                        }),
                    },
                },
                {
                    onSettled: () => {
                        setMediaTools({
                            edit: null,
                            type: null,
                            id: null,
                            record: null,
                        });
                        refresh();
                    },
                    onError: (e) => {
                        handleHttpError(e, notify);
                    },
                }
            );
        }
    };

    const onClickFx = (e, edit) => {
        e.preventDefault();
        setMediaTools({
            ...mediaTools,
            edit: edit,
            label: `equip.${edit ? "editMedia" : "showMedia"}`,
        });
    };

    if (isLoading) {
        return <></>;
    }
    return (
        <Dialog
            open={mediaTools ? true : false}
            fullWidth
            fullScreen={isFullscreen}
            slotProps={{
                paper: {
                    sx: {
                        display: "flex",
                        flexDirection: "column",
                        ...(isFullscreen && { height: "100vh" }),
                    },
                }
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 2,
                    px: 4,
                    flexShrink: 0,
                }}
            >
                <Typography variant="body1">{translate(mediaTools.label)}</Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Button
                            startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            title={translate(isFullscreen ? "ra.action.close" : "ra.action.expand")}
                            size="small"
                        >
                            {translate(`equip.${isFullscreen ? "exitFullScreen" : "fullScreen"}`)}
                        </Button>
                    </Box>

                    {mediaTools.edit &&
                    mediaTools.id &&
                    permissions.vote({
                        resource: "media",
                        attribute: "read",
                        subject: mediaTools.record,
                    }) ? (
                        <ShowButton
                            onClick={(e) => onClickFx(e, false)}
                            title={translate("equip.show")}
                        />
                    ) : (
                        mediaTools.id &&
                        permissions.vote({
                            resource: "media",
                            attribute: "edit",
                            subject: mediaTools.record,
                        }) && (
                            <EditButton
                                title={translate("equip.edit")}
                                onClick={(e) => onClickFx(e, true)}
                            />
                        )
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                    "& form": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    },
                }}
            >
                {mediaTools.edit &&
                    permissions.vote({
                        resource: "media",
                        attribute: "edit",
                        subject: mediaTools.record,
                    }) && (
                        <EditView
                            createMedia={createMedia}
                            mediaTools={mediaTools}
                            setMediaTools={setMediaTools}
                            mediaImages={mediaImages}
                            isFullscreen={isFullscreen}
                        />
                    )}

                {!mediaTools.edit &&
                    permissions.vote({
                        resource: "media",
                        attribute: "read",
                        subject: mediaTools.record,
                    }) && (
                        <ShowView
                            mediaTools={mediaTools}
                            setMediaTools={setMediaTools}
                            isFullscreen={isFullscreen}
                        />
                    )}
            </Box>
        </Dialog>
    );
};
